/**
 * SystemSnapshotCard — Phase E render of the pre-fetched system snapshot.
 *
 * Renders the `SystemSnapshot` Pydantic schema (mirror in
 * `frontend/src/types/investigation.ts`) as numbers + tables; no charts in
 * v1 (deferred to v2 which will add a charts package). Card layout matches
 * the existing parsed-anchor / tool-timeline card pattern in
 * `InvestigationDetailPage.tsx` for visual consistency.
 *
 * UI rules applied (frontend/.claude/skills/ui-ux-pro-max/SKILL.md — read
 * before writing this component):
 *   - 8dp spacing rhythm via `theme.spacing(N)` (sx `p`/`mb`/`spacing` props
 *     all use integer multiples).
 *   - MUI `<Typography>` variants throughout — no raw `<h1>`/`<p>` nodes.
 *   - Semantic table markup (`<TableContainer><Table>`); compact density
 *     (`size="small"`) — engineers want 30 rows on screen, not 8.
 *   - Color comes from `theme.palette.*` only; severity for the errors
 *     banner uses `<Alert severity="warning">`.
 *   - No interactive elements yet → no touch-target concern; if a future
 *     iteration adds a "drill into host" link, give it `role="button"` +
 *     `minHeight: 44, minWidth: 44` per the rules.
 *
 * Three-state rule (per ui-ux-pro-max):
 *   - Loading: caller passes `null`; the card is hidden entirely (the
 *     snapshot is a one-shot SSE event, not a fetched-on-mount surface,
 *     so no skeleton needed — page renders fine without it).
 *   - Empty: when ALL data sections are empty AND `errors` is non-empty,
 *     we render the errors-only banner; when everything is empty (no
 *     errors either — DD totally cold) we render a single "snapshot
 *     collected, no data returned" line.
 *   - Error: per-collection failures from the snapshot collector are
 *     surfaced in the bottom alert; a hard fetch error never reaches
 *     this component (the runner downgrades to an empty bundle).
 */

import { useState } from 'react';

import ErrorIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import type {
  EndpointStatRow,
  InsightRow,
  InsightSeverity,
  LogSourceBucket,
  SystemSnapshot,
} from '@/types/investigation';
import { formatRelative } from '@/utils/formatRelative';
import { RetroAccordion } from '@/components/RetroAccordion';

interface SystemSnapshotCardProps {
  snapshot: SystemSnapshot | null;
}

const TOP_QUERIES_TO_RENDER = 3;
const DASH = '—';

function fmtNumber(value: number | null, options?: Intl.NumberFormatOptions): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return value.toLocaleString(undefined, options ?? { maximumFractionDigits: 1 });
}

function fmtPercent(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return `${value.toFixed(1)}%`;
}

function fmtBytesPerSec(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  // The DD metric is bytes/sec; values commonly sit in KB/MB ranges. Pick the
  // smallest unit that produces a number ≥ 1.
  const units = [
    { unit: 'GB/s', factor: 1024 ** 3 },
    { unit: 'MB/s', factor: 1024 ** 2 },
    { unit: 'KB/s', factor: 1024 },
    { unit: 'B/s', factor: 1 },
  ];
  for (const { unit, factor } of units) {
    if (value >= factor) return `${(value / factor).toFixed(1)} ${unit}`;
  }
  return `${value.toFixed(0)} B/s`;
}

function fmtIORate(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  // DD `system.io.{r,w}_s` units are operations/sec.
  return `${fmtNumber(value)}/s`;
}

function fmtIso(iso: string): string {
  // 2026-05-09T18:31:33Z → 2026-05-09 18:31 UTC. Compact for the caption.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

function shortSig(signature: string): string {
  // DBM signatures are 16-char hex; show first 8 in the tight table.
  return signature.length > 8 ? signature.slice(0, 8) : signature;
}

type EndpointSortKey =
  | 'service'
  | 'resource_name'
  | 'http_method'
  | 'request_count'
  | 'p50_ms'
  | 'p95_ms'
  | 'p99_ms'
  | 'error_rate';

const SEVERITY_PALETTE: Record<InsightSeverity, string> = {
  info: 'info.main',
  warn: 'warning.main',
  critical: 'error.main',
};

function severityIcon(severity: InsightSeverity): React.ReactElement {
  if (severity === 'critical') {
    return <ErrorIcon fontSize="small" sx={{ color: SEVERITY_PALETTE.critical }} />;
  }
  if (severity === 'warn') {
    return <WarningIcon fontSize="small" sx={{ color: SEVERITY_PALETTE.warn }} />;
  }
  return <InfoIcon fontSize="small" sx={{ color: SEVERITY_PALETTE.info }} />;
}

function levelChipColor(level: string): 'default' | 'warning' | 'error' {
  if (level === 'error') return 'error';
  if (level === 'warn') return 'warning';
  return 'default';
}

function compareEndpoints(
  a: EndpointStatRow,
  b: EndpointStatRow,
  key: EndpointSortKey,
  desc: boolean,
): number {
  const va = a[key];
  const vb = b[key];
  // Push nulls/undefineds to the bottom regardless of direction.
  if (va === null || va === undefined) return 1;
  if (vb === null || vb === undefined) return -1;
  if (typeof va === 'number' && typeof vb === 'number') {
    return desc ? vb - va : va - vb;
  }
  return desc
    ? String(vb).localeCompare(String(va))
    : String(va).localeCompare(String(vb));
}

interface InsightsCalloutProps {
  insights: InsightRow[];
}

function InsightsCallout({ insights }: InsightsCalloutProps): React.ReactElement {
  return (
    <Box
      role="region"
      aria-label="AI insights for this snapshot"
      sx={{
        bgcolor: 'action.selected',
        borderRadius: 1,
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        AI insights
      </Typography>
      <Stack spacing={1.5}>
        {insights.map((insight, idx) => (
          <Stack key={`insight-${idx}`} direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ pt: 0.25 }}>{severityIcon(insight.severity)}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {insight.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: 'break-word' }}
              >
                {insight.detail}
              </Typography>
              {insight.references.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontFamily: 'monospace',
                    mt: 0.5,
                    wordBreak: 'break-word',
                  }}
                >
                  {insight.references.join(' · ')}
                </Typography>
              )}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

interface TopEndpointsTableProps {
  rows: EndpointStatRow[];
}

function TopEndpointsTable({ rows }: TopEndpointsTableProps): React.ReactElement {
  const [sortKey, setSortKey] = useState<EndpointSortKey>('request_count');
  const [sortDesc, setSortDesc] = useState<boolean>(true);

  const sorted = [...rows].sort((a, b) => compareEndpoints(a, b, sortKey, sortDesc));

  const handleSort = (key: EndpointSortKey): void => {
    if (key === sortKey) {
      setSortDesc((d) => !d);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  const headerCell = (
    label: string,
    key: EndpointSortKey,
    align: 'left' | 'right' = 'left',
  ): React.ReactElement => (
    <TableCell align={align}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key && !sortDesc ? 'asc' : 'desc'}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <TableContainer>
      <Table
        size="small"
        aria-label="Top API endpoints in the investigation window"
      >
        <TableHead>
          <TableRow>
            {headerCell('Service', 'service')}
            {headerCell('Endpoint', 'resource_name')}
            {headerCell('Method', 'http_method')}
            {headerCell('Calls', 'request_count', 'right')}
            {headerCell('p50', 'p50_ms', 'right')}
            {headerCell('p95', 'p95_ms', 'right')}
            {headerCell('p99', 'p99_ms', 'right')}
            {headerCell('Err rate', 'error_rate', 'right')}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, idx) => (
            <TableRow key={`${row.service}::${row.resource_name}::${idx}`}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{row.service}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                {row.resource_name}
              </TableCell>
              <TableCell>{row.http_method ?? DASH}</TableCell>
              <TableCell align="right">
                {fmtNumber(row.request_count, { maximumFractionDigits: 0 })}
              </TableCell>
              <TableCell align="right">
                {row.p50_ms === null ? DASH : `${fmtNumber(row.p50_ms)} ms`}
              </TableCell>
              <TableCell align="right">
                {row.p95_ms === null ? DASH : `${fmtNumber(row.p95_ms)} ms`}
              </TableCell>
              <TableCell align="right">
                {row.p99_ms === null ? DASH : `${fmtNumber(row.p99_ms)} ms`}
              </TableCell>
              <TableCell align="right">{(row.error_rate * 100).toFixed(1)}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface LogsBySourceAccordionProps {
  buckets: LogSourceBucket[];
}

type LogLevelFilter = 'all' | 'error' | 'warn' | 'info';

function bucketCountForLevel(bucket: LogSourceBucket, level: LogLevelFilter): number {
  switch (level) {
    case 'error':
      return bucket.error_count;
    case 'warn':
      return bucket.warn_count;
    case 'info':
      return bucket.info_count;
    case 'all':
    default:
      return bucket.total;
  }
}

function LogsBySourceAccordion({
  buckets,
}: LogsBySourceAccordionProps): React.ReactElement {
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [levelFilter, setLevelFilter] = useState<LogLevelFilter>('all');

  const toggle = (source: string): void => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) {
        next.delete(source);
      } else {
        next.add(source);
      }
      return next;
    });
  };

  // Phase K — level filter: hide buckets with zero matching events; samples
  // inside an expanded bucket also respect the active filter. 'all' keeps
  // every bucket and every sample.
  const visibleBuckets = buckets.filter(
    (bucket) => bucketCountForLevel(bucket, levelFilter) > 0,
  );

  const filterChips: { value: LogLevelFilter; label: string }[] = [
    { value: 'all', label: 'all' },
    { value: 'error', label: 'error' },
    { value: 'warn', label: 'warn' },
    { value: 'info', label: 'info' },
  ];

  return (
    <Stack spacing={1}>
      {/* Level filter — chip group at the top of the section. Click toggles
          the filter; non-active chips are outlined, active is filled. */}
      <Box
        role="radiogroup"
        aria-label="Filter log samples by level"
        sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          filter
        </Typography>
        {filterChips.map((chip) => {
          const active = levelFilter === chip.value;
          return (
            <Chip
              key={chip.value}
              label={chip.label}
              size="small"
              role="radio"
              aria-checked={active}
              variant={active ? 'filled' : 'outlined'}
              color={
                active && chip.value === 'error'
                  ? 'error'
                  : active && chip.value === 'warn'
                  ? 'warning'
                  : active && chip.value === 'info'
                  ? 'info'
                  : 'default'
              }
              onClick={() => setLevelFilter(chip.value)}
              sx={{ fontFamily: 'monospace', minHeight: 28 }}
            />
          );
        })}
        {visibleBuckets.length === 0 && levelFilter !== 'all' && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            no sources have {levelFilter}-level samples in this window
          </Typography>
        )}
      </Box>

      {visibleBuckets.map((bucket) => {
        const isOpen = expandedSources.has(bucket.source);
        // Samples filtered to the active level when not 'all'.
        const visibleSamples =
          levelFilter === 'all'
            ? bucket.samples
            : bucket.samples.filter((s) => s.level === levelFilter);
        return (
          <Box
            key={bucket.source}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Box
              role="button"
              aria-expanded={isOpen}
              aria-controls={`logs-source-panel-${bucket.source}`}
              tabIndex={0}
              onClick={() => toggle(bucket.source)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle(bucket.source);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 44,
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
                {bucket.source}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                total {bucket.total}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {bucket.error_count > 0 && (
                <Chip
                  size="small"
                  color="error"
                  label={`error ${bucket.error_count}`}
                  sx={{ fontFamily: 'monospace' }}
                />
              )}
              {bucket.warn_count > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`warn ${bucket.warn_count}`}
                  sx={{ fontFamily: 'monospace' }}
                />
              )}
              {bucket.info_count > 0 && (
                <Chip
                  size="small"
                  label={`info ${bucket.info_count}`}
                  sx={{ fontFamily: 'monospace' }}
                />
              )}
              <IconButton
                size="small"
                aria-label={isOpen ? 'Collapse log samples' : 'Expand log samples'}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(bucket.source);
                }}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
            <Collapse in={isOpen} unmountOnExit>
              <Box
                id={`logs-source-panel-${bucket.source}`}
                sx={{ px: 2, pb: 2, pt: 0 }}
              >
                {/* Sample count footer — gives users a sense of how many
                    samples they're seeing vs. the bucket's total events.
                    "showing N of M samples (filter: <level>)" when filtered. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  showing {visibleSamples.length} of {bucket.samples.length} captured
                  {bucket.samples.length < bucket.total
                    ? ` · ${bucket.total} events total`
                    : ''}
                  {levelFilter !== 'all' ? ` · level: ${levelFilter}` : ''}
                </Typography>
                {visibleSamples.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {bucket.samples.length === 0
                      ? 'No samples captured for this source.'
                      : `No ${levelFilter}-level samples for this source.`}
                  </Typography>
                ) : (
                  // Phase K — scrollable container: cap the expanded body at
                  // ~480 px so a chatty source (10+ samples) doesn't push the
                  // rest of the card off-screen. The bucket-row chevron
                  // toggles the whole section; this scroll lets the user
                  // browse all samples without collapsing.
                  <Box
                    sx={{
                      maxHeight: 480,
                      overflowY: 'auto',
                      pr: 1,
                    }}
                  >
                    <Stack spacing={1}>
                      {visibleSamples.map((sample, idx) => (
                        <Box
                          key={`${bucket.source}-sample-${idx}`}
                          sx={{
                            bgcolor: 'action.hover',
                            borderRadius: 0.5,
                            p: 1,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Chip
                              size="small"
                              color={levelChipColor(sample.level)}
                              label={sample.level}
                              sx={{ fontFamily: 'monospace' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {sample.timestamp}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {sample.message}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}

export function SystemSnapshotCard({
  snapshot,
}: SystemSnapshotCardProps): React.ReactElement | null {
  if (!snapshot) return null;

  const hasHosts = snapshot.host_metrics.length > 0;
  const hasDb = snapshot.db_metrics.hosts.length > 0;
  const hasGunicorn =
    snapshot.gunicorn !== null &&
    (snapshot.gunicorn.workers_avg !== null || snapshot.gunicorn.requests_per_sec !== null);
  const hasNginx5xx = snapshot.nginx_5xx_count !== null;
  const hasSlowQueries = snapshot.top_slow_queries.length > 0;
  const hasErrors = snapshot.errors.length > 0;
  // Phase H/I/G fields default to [] so older snapshots with no
  // top_endpoints / logs_by_source / insights still render fine.
  const topEndpoints = snapshot.top_endpoints ?? [];
  const logsBySource = snapshot.logs_by_source ?? [];
  const insights = snapshot.insights ?? [];
  const hasTopEndpoints = topEndpoints.length > 0;
  const hasLogsBySource = logsBySource.length > 0;
  const hasInsights = insights.length > 0;
  const hasAnyData =
    hasHosts ||
    hasDb ||
    hasGunicorn ||
    hasNginx5xx ||
    hasSlowQueries ||
    hasTopEndpoints ||
    hasLogsBySource;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        bgcolor: 'background.paper',
      }}
    >
      {/* ---- AI insights (Phase G) — at the very top of the card. ---- */}
      {hasInsights && <InsightsCallout insights={insights} />}

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">System snapshot</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Investigation window: {fmtIso(snapshot.anchor_from_ts)} →{' '}
          {fmtIso(snapshot.anchor_to_ts)} · snapshot collected{' '}
          {formatRelative(snapshot.collected_at)}
        </Typography>
      </Box>

      {!hasAnyData && !hasErrors && (
        <Typography variant="body2" color="text.secondary">
          Snapshot collected; no data returned for this window.
        </Typography>
      )}

      {/* ---- Hosts ---- */}
      {hasHosts && (
        <RetroAccordion
          title="Hosts"
          subtitle={`${snapshot.host_metrics.length} host${snapshot.host_metrics.length !== 1 ? 's' : ''}`}
          defaultOpen={false}
        >
          <Box sx={{ pb: 2 }}>
            <TableContainer>
              <Table size="small" aria-label="Per-host CPU, memory, I/O, and network averages">
                <TableHead>
                  <TableRow>
                    <TableCell>Host</TableCell>
                    <TableCell align="right">CPU avg</TableCell>
                    <TableCell align="right">Mem avg</TableCell>
                    <TableCell align="right">I/O read</TableCell>
                    <TableCell align="right">I/O write</TableCell>
                    <TableCell align="right">Net rcvd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {snapshot.host_metrics.map((row) => (
                    <TableRow key={row.host}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{row.host}</TableCell>
                      <TableCell align="right">{fmtPercent(row.cpu_user_avg)}</TableCell>
                      <TableCell align="right">{fmtPercent(row.mem_used_avg)}</TableCell>
                      <TableCell align="right">{fmtIORate(row.io_read_rate)}</TableCell>
                      <TableCell align="right">{fmtIORate(row.io_write_rate)}</TableCell>
                      <TableCell align="right">{fmtBytesPerSec(row.net_bytes_rcvd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </RetroAccordion>
      )}

      {/* ---- Database ---- */}
      {hasDb && (
        <RetroAccordion title="Database" defaultOpen={false}>
          <Box sx={{ pb: 2 }}>
            <TableContainer>
              <Table size="small" aria-label="Per-Postgres-host connection and cache headline metrics">
                <TableHead>
                  <TableRow>
                    <TableCell>Host</TableCell>
                    <TableCell align="right">Conn (active / max)</TableCell>
                    <TableCell align="right">Conn util</TableCell>
                    <TableCell align="right">Buffer hit</TableCell>
                    <TableCell align="right">Replica lag</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {snapshot.db_metrics.hosts.map((row) => (
                    <TableRow key={row.host}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{row.host}</TableCell>
                      <TableCell align="right">
                        {fmtNumber(row.connections_active, { maximumFractionDigits: 0 })}
                        {' / '}
                        {fmtNumber(row.connections_max, { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right">{fmtPercent(row.connections_utilization_pct)}</TableCell>
                      <TableCell align="right">{fmtPercent(row.buffer_hit_ratio_pct)}</TableCell>
                      <TableCell align="right">
                        {row.replica_lag_seconds === null
                          ? DASH
                          : `${fmtNumber(row.replica_lag_seconds)}s`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hasSlowQueries && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Top slow queries
                </Typography>
                <Stack spacing={1}>
                  {snapshot.top_slow_queries.slice(0, TOP_QUERIES_TO_RENDER).map((row, idx) => (
                    <Box key={row.signature || `slow-${idx}`}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontFamily: 'monospace' }}
                      >
                        {idx + 1}. <Box component="span" sx={{ color: 'text.primary' }}>{shortSig(row.signature)}</Box>
                        {row.db && ` · db:${row.db}`}
                        {' · '}
                        {fmtNumber(row.total_time_ms)} ms total
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                          bgcolor: 'action.hover',
                          borderRadius: 0.5,
                          p: 1,
                          mt: 0.5,
                        }}
                      >
                        {row.sql}
                        {row.sql_truncated ? '…' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </RetroAccordion>
      )}

      {/* ---- Frontline (gunicorn + nginx) ---- */}
      {(hasGunicorn || hasNginx5xx) && (
        <RetroAccordion title="Frontline" subtitle="gunicorn + nginx" defaultOpen={false}>
          <Box sx={{ pb: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              {hasGunicorn && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    gunicorn workers (avg)
                  </Typography>
                  <Typography variant="body1">
                    {fmtNumber(snapshot.gunicorn?.workers_avg ?? null, { maximumFractionDigits: 1 })}
                  </Typography>
                </Box>
              )}
              {hasGunicorn && snapshot.gunicorn?.requests_per_sec !== null && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    gunicorn req/s
                  </Typography>
                  <Typography variant="body1">
                    {fmtNumber(snapshot.gunicorn?.requests_per_sec ?? null)}
                  </Typography>
                </Box>
              )}
              {hasNginx5xx && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    nginx 5xx count
                  </Typography>
                  <Typography
                    variant="body1"
                    color={(snapshot.nginx_5xx_count ?? 0) > 0 ? 'error.main' : undefined}
                  >
                    {fmtNumber(snapshot.nginx_5xx_count, { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </RetroAccordion>
      )}

      {/* ---- Top API endpoints (Phase H) ---- */}
      {hasTopEndpoints && (
        <RetroAccordion title="Top API endpoints" defaultOpen={false}>
          <Box sx={{ pb: 2 }}>
            <TopEndpointsTable rows={topEndpoints} />
          </Box>
        </RetroAccordion>
      )}

      {/* ---- Logs by source (Phase I) ---- */}
      {hasLogsBySource && (
        <RetroAccordion title="Logs by source" defaultOpen={false}>
          <Box sx={{ pb: 2 }}>
            <LogsBySourceAccordion buckets={logsBySource} />
          </Box>
        </RetroAccordion>
      )}

      {/* ---- Collection errors (always last) ---- */}
      {hasErrors && (
        <Alert severity="warning" sx={{ mt: 3 }} role="alert">
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Snapshot collected with {snapshot.errors.length} partial failure
            {snapshot.errors.length === 1 ? '' : 's'}:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {snapshot.errors.map((err, idx) => (
              <Typography
                key={`err-${idx}`}
                component="li"
                variant="caption"
                sx={{ fontFamily: 'monospace', display: 'list-item' }}
              >
                {err}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}
    </Box>
  );
}
